import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  calculateDistance,
  getMidpoint,
  extractCoordinates,
} from '../../utils/geometry'

interface Layer {
  id: string
  enabled: boolean
  visible?: boolean
}

interface UseParcelLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  geoJson?: any
  layers: Layer[]
  styleLoadedCount: number
}

/**
 * Hook to handle parcel boundary layers and dimension metrics
 */
export const useParcelLayers = ({
  map,
  mapLoaded,
  geoJson,
  layers,
  styleLoadedCount,
}: UseParcelLayersProps) => {
  // Handle parcel boundaries rendering from geoJson
  useEffect(() => {
    if (!map.current || !mapLoaded || !geoJson) return

    const parcelLayer = layers.find((layer) => layer.id === 'parcel-boundaries')
    const isVisible = parcelLayer?.visible ?? parcelLayer?.enabled ?? false

    try {
      // Add source if it doesn't exist
      if (!map.current.getSource('parcel-boundaries-source')) {
        map.current.addSource('parcel-boundaries-source', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: geoJson,
            properties: {},
          },
        })
      }

      // Add layer if it doesn't exist
      if (!map.current.getLayer('parcel-boundaries-layer')) {
        map.current.addLayer({
          id: 'parcel-boundaries-layer',
          type: 'line',
          source: 'parcel-boundaries-source',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#00ADE6',
            'line-width': 3,
            'line-opacity': 0.8,
          },
        })

        // Add fill layer for the parcel
        map.current.addLayer({
          id: 'parcel-boundaries-fill',
          type: 'fill',
          source: 'parcel-boundaries-source',
          paint: {
            'fill-color': '#00ADE6',
            'fill-opacity': 0.1,
          },
        })
      }

      // Update visibility based on layer state
      if (map.current.getLayer('parcel-boundaries-layer')) {
        map.current.setLayoutProperty(
          'parcel-boundaries-layer',
          'visibility',
          isVisible ? 'visible' : 'none'
        )
      }
      if (map.current.getLayer('parcel-boundaries-fill')) {
        map.current.setLayoutProperty(
          'parcel-boundaries-fill',
          'visibility',
          isVisible ? 'visible' : 'none'
        )
      }
    } catch (error) {
      console.warn('Failed to add parcel boundaries:', error)
    }
  }, [geoJson, layers, mapLoaded, styleLoadedCount])

  // Handle dimension metrics for parcel boundaries
  useEffect(() => {
    if (!map.current || !mapLoaded || !geoJson) return

    const dimensionLayer = layers.find(
      (layer) => layer.id === 'land-dimension-metrics'
    )
    const isVisible =
      dimensionLayer?.visible ?? dimensionLayer?.enabled ?? false

    try {
      // Extract coordinates from geoJson
      const coordinates = extractCoordinates(geoJson)

      if (coordinates.length === 0) return

      // Get the outer ring
      const outerRing = coordinates[0]

      // Create dimension labels for each segment
      const dimensionLabels: any[] = []
      for (let i = 0; i < outerRing.length - 1; i++) {
        const start = outerRing[i]
        const end = outerRing[i + 1]
        const distance = calculateDistance(start, end)
        const midpoint = getMidpoint(start, end)

        dimensionLabels.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: midpoint,
          },
          properties: {
            distance: distance.toFixed(2),
            distanceLabel: `${distance.toFixed(1)}m`,
          },
        })
      }

      // Add dimension metrics source
      if (!map.current.getSource('dimension-metrics-source')) {
        map.current.addSource('dimension-metrics-source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: dimensionLabels,
          },
        })
      } else {
        // Update existing source
        const source = map.current.getSource(
          'dimension-metrics-source'
        ) as mapboxgl.GeoJSONSource
        source.setData({
          type: 'FeatureCollection',
          features: dimensionLabels,
        })
      }

      // Add or remove dimension metrics layer based on visibility
      const layerExists = map.current.getLayer('dimension-metrics-layer')

      if (isVisible && !layerExists) {
        // Add layer when it should be visible and doesn't exist
        map.current.addLayer({
          id: 'dimension-metrics-layer',
          type: 'symbol',
          source: 'dimension-metrics-source',
          layout: {
            'text-field': ['get', 'distanceLabel'],
            'text-size': 12,
            'text-offset': [0, 0],
            'text-anchor': 'center',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#00ADE6',
            'text-halo-color': '#FFFFFF',
            'text-halo-width': 2,
          },
        })
      } else if (!isVisible && layerExists) {
        // Remove layer when it should be hidden and exists
        map.current.removeLayer('dimension-metrics-layer')
      }
    } catch (error) {
      console.warn('Failed to add dimension metrics:', error)
    }
  }, [geoJson, layers, mapLoaded, styleLoadedCount])
}
